import * as d3 from "d3";
import { defaultWidth, defaultHeight } from "../defaults.js";

// ...existing code...
export const drawMergeLines = (
  container,
  thread,
  terminal,
  separation,
  onClick
) => {
  const ancestor = container.node().parentNode;

  let mergeLayer = d3.select(ancestor).select(`.merge-layer-${thread.id}`);
  if (mergeLayer.empty()) {
    mergeLayer = d3
      .select(ancestor)
      .append("g")
      .classed("merge-layer", true)
      .classed(`merge-layer-${thread.id}`, true);
  }

  // Clear out unmerged threads
  if (thread.status !== "merged") {
    // Remove existing merge lines for this thread
    mergeLayer.selectAll(`path.merge-line-${thread.id}`).remove();
    return;
  }
  // Although merges are semantically the same wherever they occur,
  // we will make a visual distinction if the from thread is above the
  // to thread or below it. If below, we perceive the merge as a loop
  // back upwards; if above, we draw a straight(ish) line down to the target.

  // Path segments for the loop back:
  // 1. Descend vertically from start
  // 2. Curve left
  // 3. Move horizontally to the left
  // 4. Curve up
  // 5. Move vertically up
  // 6. Curve right
  // 7. Move horizontally to the right
  // 8. Curve to the merge point

  // Create a curved path that goes from fromPosition to toPosition
  // Use bezier curves, and lines to create a smooth loop back

  const startX = thread.position.x + terminal.position.x + defaultWidth / 2;
  const startY = thread.position.y + terminal.position.y + defaultHeight;

  const verticalEndY = startY + defaultHeight;

  //  Vertical line up to near toPosition
  const upY = separation.dy + defaultHeight * 2 - 20;
  const downY = separation.dy + defaultHeight * 2 + defaultHeight * 2;
  // Line lengths so merge loop clears other elements
  const lineOut = separation.dx > 0 ? -separation.dx - 30 : -30;
  const lineIn = separation.dx > 0 ? 30 : -separation.dx + 30;

  const xOffset = separation.dx > 0 ? -defaultWidth / 2 : defaultWidth / 2;
  const lineDownX = -separation.dx - xOffset;

  const pathData =
    separation.dy >= 0
      ? `M ${startX} ${startY} V${verticalEndY}
                   c ${0} ${10}, 
                     ${-10} ${20}, 
                     ${-20} ${20}
                   l ${lineOut} ${0}
                   c ${0} ${0},
                     ${-20} ${0},
                     ${-20} ${-20}
                   l ${0} ${-upY}
                   c ${0} ${-10},
                     ${10} ${-20},
                     ${20} ${-20}
                   l ${lineIn} ${0}
                   c ${0} ${0},
                     ${20} ${0},
                     ${20} ${10}`
      : `M ${startX} ${startY} V${verticalEndY}
                     c ${0} ${10}, 
                     ${0} ${20}, 
                     ${lineDownX} ${-downY} 
                     c ${xOffset / 4} ${2},
                     ${xOffset / 2} ${5},
                     ${xOffset} ${defaultHeight}`;

  // Draw the merge line as a path on the lines layer (behind other elements)
  // Use thread-specific selector to avoid conflicts with other merge lines.
  // Merge lines aren't tremendously interesting in themselves, so we select the
  // thread data itself.

  const mergeLine = mergeLayer
    .selectAll(`path.merge-line-${thread.id}`)
    .data([thread]);

  mergeLine
    .enter()
    .append("path")
    .attr("class", "merge-line")
    .classed(`merge-line-${thread.id}`, true)
    .attr("data-id", `${thread.id}`)
    .merge(mergeLine)
    .attr("d", pathData)
    .attr("fill", "none")
    .attr("cursor", "grab")
    .on("click", (event, d) => {
      event.stopPropagation(); // Prevent event propagation
      console.log("onClick merge", d);
      onClick(d);
    });

  mergeLine.exit().remove();
};

export const drawLoopLines = (
  container,
  thread,
  from,
  to,
  separation,
  onClick
) => {
  // Get or create the lines layer specific to this thread to ensure loops are drawn behind other elements.
  // Loop lines always start from subthreads of a refinement so
  // we just need to add the x-offset to get to right start point.
  const ancestor = container.node().parentNode;
  let loopLayer = d3.select(ancestor).select(`.loop-layer-${thread.id}`);
  if (loopLayer.empty()) {
    loopLayer = d3
      .select(ancestor)
      .append("g")
      .classed("loop-layer", true)
      .classed(`loop-layer-${thread.id}`, true);
  }

  // Create a curved path that goes from fromPosition to toPosition
  // Use bezier curves, and lines to create a smooth loop back

  const startX = thread.position.x + from.position.x + defaultWidth / 2;
  const startY = thread.position.y + from.position.y + defaultHeight;

  const verticalEndY = startY + defaultHeight;
  // Vertical line up to near toPosition
  const upY = separation.dy + (from ? defaultHeight * 2 : 0) + 5;

  const lineIn = separation.dx + 5;

  const pathData = `M ${startX} ${startY} V${verticalEndY}
                     c ${0} ${10}, 
                     ${10} ${20}, 
                     ${20} ${20}
                   l ${5} ${0}
                   c ${0} ${0},
                     ${20} ${0},
                     ${20} ${-20}
                   l ${0} ${-upY}
                   c ${0} ${-10},
                     ${-10} ${-20},
                     ${-20} ${-20}
                   l ${-lineIn} ${0}
                   c ${0} ${0},
                     ${-20} ${0},
                     ${-20} ${10}`;

  // Draw the loop line as a path on the lines layer (behind other elements)
  // Use thread-specific selector to avoid conflicts with other loop lines.
  // Loop lines aren't tremendously interesting in themselves, so we select the
  // thread data itself.
  const loopLine = loopLayer
    .selectAll(`path.loop-line-${thread.id}`)
    .data([thread]);

  loopLine
    .enter()
    .append("path")
    .attr("class", "loop-line")
    .classed(`loop-line-${thread.id}`, true)
    .attr("data-id", `${thread.id}`)
    .merge(loopLine)
    .attr("d", pathData)
    .attr("fill", "none")
    .attr("cursor", "grab")
    .on("click", (event, d) => {
      event.stopPropagation(); // Prevent event propagation
      console.log("onClick loop", d);
      onClick(d);
    });

  loopLine.exit().remove();
};

export const drawStateLines = (
  container,
  threadStatus,
  threadShowLoop,
  threadPoststate,
  farY,
  children,
  states,
  onDrag,
  onClick
) => {
  const stateData = [];
  if ((!children || children.length === 0) && threadShowLoop) {
    return;
  }
  children.forEach((activity, index) => {
    const isFirst = index === 0;
    const isLast = index === children.length - 1;

    // ✅ First activity: Add a line from a default `y = -20`
    if (isFirst) {
      stateData.push({
        id: activity.prestate_id || `first-state-${activity.id}`,
        from: {
          x: activity.position.x + defaultWidth / 2, // Centered horizontally
          y: 20, // Fixed position above
        },
        to: {
          x: activity.position.x + defaultWidth / 2,
          y: activity.position.y, // Top center of the first activity
        },
        state: states.find((s) => s.id === activity.prestate_id) || {},
      });
    }

    // ✅ Standard activity transitions
    if (index > 0) {
      const prevActivity = children[index - 1];
      const nudge = prevActivity.class === "refinement" ? defaultHeight : 0;

      stateData.push({
        id: activity.prestate_id,
        from: {
          x: prevActivity.position.x + defaultWidth / 2,
          y: prevActivity.position.y + prevActivity.size.height + nudge, // Bottom center of previous activity
        },
        to: {
          x: prevActivity.position.x + defaultWidth / 2, // ensure verticality as refinements throw this off
          y: activity.position.y, // Top center of next activity
        },
        state: states.find((s) => s.id === activity.prestate_id) || {},
      });
    }

    // ✅ Last activity
    if (isLast) {
      if (
        !["looped", "merged"].includes(threadStatus) ||
        (threadStatus === "looped" && !threadShowLoop)
      ) {
        const endstate =
          states.find((s) => s.id === activity.poststate_id) || {};
        const nudge =
          activity.class === "refinement" // && endstate.label === "END"
            ? defaultHeight
            : activity.class === "refinement" //&& endstate.id
              ? defaultHeight / 2
              : 0; // Thread end labels need a little nudge

        stateData.push({
          id: activity.poststate_id || undefined,
          from: {
            x: activity.position.x + defaultWidth / 2,
            y: activity.position.y + activity.size.height + nudge, // Bottom center of last activity
          },
          to: {
            x: activity.position.x + defaultWidth / 2,
            y: farY
              ? farY // Subthreads go to their return line (if attached)
              : activity.position.y +
                activity.size.height +
                defaultHeight +
                nudge, // Extend downward by defaultHeight
          },
          state: endstate,
        });
      }
    }
  });

  // Join state lines to D3
  const stateLines = container
    .selectAll("g.state-group")
    .data(stateData, (d) => d.id)
    .join(
      (enter) =>
        enter
          .append("g")
          .attr("class", "state-group")
          .attr("data-id", (d) => d.id)
          .attr("cursor", "grab")
          .attr("data-class", (d) => d.class || "state")
          .attr("data-type", (d) => d.type || "state")

          .on("click", (event, d) => {
            event.stopPropagation(); // Prevent event propagation
            console.log("onClick state", d.state);
            onClick(d.state);
          }),
      (update) => update,
      (exit) => exit.remove()
    );

  // Draw state lines
  stateLines.each(function (d) {
    const stateContainer = d3.select(this);
    drawStateLine(stateContainer, d || {}, onClick);
    if (d.id) drawStateDescription(stateContainer, d, onClick);
  });

  stateLines.attr(
    "transform",
    (d) => `translate(${d.from.x || 0}, ${d.from.y || 0})`
  );
};

export const drawStateLine = (container, data) => {
  const stateline = container.selectAll("line.state").data([data]);

  stateline
    .enter()
    .append("line")
    .classed("state", true)
    .merge(stateline)
    .attr("x1", 0)
    .attr("y1", 0)
    .attr("x2", (d) => d.to.x - d.from.x)
    .attr("y2", (d) => d.to.y - d.from.y);

  stateline.exit().remove();
};

export const drawStateDescription = (container, data) => {
  const stateDesc = container.selectAll("g.state-description").data([data]);

  const enterDesc = stateDesc
    .enter()
    .append("g")
    .attr("class", "state-description")
    .attr("data-class", (d) => d.class || "state")
    .attr("data-type", (d) => d.type || "state")
    .attr("data-id", (d) => d.id);

  const mergedDesc = enterDesc.merge(stateDesc);

  mergedDesc
    .attr(
      "transform",
      `translate(${(data.to.x - data.from.x) / 2}, ${(data.to.y - data.from.y) / 2})`
    )
    .classed("show", data.state.show);

  stateDesc.exit().remove();

  mergedDesc
    .selectAll("text.state-label")
    .data([data])
    .join("text")
    .attr("class", "state-label")
    .attr("x", 10)
    .attr("y", 0)
    .attr("dy", "0.35em")
    .attr("font-size", "12px")
    .attr("font-style", "italic")
    .text((d) => d.state.label || "");

  // add an ellipse to the state line

  mergedDesc
    .selectAll("ellipse.state-marker")
    .data([data])
    .join("ellipse")
    .attr("class", "state-marker")
    .attr("cx", 0)
    .attr("cy", 0)
    .attr("rx", 8)
    .attr("ry", 4);

  // add a small vertical line over the ellipse
  mergedDesc
    .selectAll("line.state-marker")
    .data([data])
    .join("line")
    .attr("class", "state-marker")
    .attr("x1", 0)
    .attr("y1", -4)
    .attr("x2", 0)
    .attr("y2", 0);
};
